import Decimal from "decimal.js";
import { SubmitErrorHandler } from "react-hook-form";

import {
  FilterEmpty,
  FilterEmptyValuesOptions,
  HandleFormValidationErrorsProps,
  InputKeyLimiterProps,
  InputPasteLimiterProps,
  ValidateResponseProps,
} from "./helperFunction.types";

import { GeneralErrorSchema } from "@/app/app.schema";
import type { GeneralErrorResponse } from "@/app/app.types";
import {
  ONE,
  PERCENT_MAX_DECIMAL_PLACES,
  ZERO,
} from "@/app/constants/constants";
import { customToast } from "@/components/alerts/CustomToast";
import { Any } from "@/interfaces/globals";
import { formatToCapitalizedWords } from "@/utils/core/generalUtils";
import { isHttps } from "@/utils/core/urlHelper";

/**
 * `selectionStart` / `selectionEnd` are often `null` for `input[type="number"]`.
 * Treating null as `0` made `afterSelection` the full value, so typing "." produced
 * ".123" instead of "123." and max-decimal validation blocked the decimal key.
 * When null/undefined, use `value.length` so the caret is treated as at the end.
 *
 * @param {HTMLInputElement} input - Target input element
 * @returns {object} Normalized selection range with `start` and `end`
 */
function getNormalizedSelectionRange(input: HTMLInputElement): {
  start: number;
  end: number;
} {
  const { value, selectionStart, selectionEnd } = input;
  const len = value.length;
  const start =
    selectionStart === null || selectionStart === undefined
      ? len
      : selectionStart;
  const end =
    selectionEnd === null || selectionEnd === undefined ? len : selectionEnd;

  return { start, end };
}

/**
 * Validates if a value's decimal places exceed the maximum allowed
 * @param {string} value - The value to validate
 * @param {number | undefined} maxDecimalPlace - Maximum allowed decimal places
 * @param {boolean} allowDecimal - Whether decimals are allowed
 * @returns {boolean} - Returns true if decimal places are valid, false if they exceed the limit
 */
function validateDecimalPlaces(
  value: string,
  maxDecimalPlace: number | undefined,
  allowDecimal: boolean,
): boolean {
  if (maxDecimalPlace === undefined || !allowDecimal || !value.includes(".")) {
    return true; // No restriction or no decimal point, allow
  }

  // If maxDecimalPlace is 0, prevent any decimal point (only integers allowed)
  if (maxDecimalPlace === 0) {
    return false;
  }

  const decimalIndex = value.indexOf(".");
  const decimalPlaces = value.substring(decimalIndex + 1).length;

  // Prevent if decimal places exceed the limit
  return decimalPlaces <= maxDecimalPlace;
}

/**
 * Sanitizes a pasted or typed decimal input string and clamps integer and fraction lengths.
 *
 * @param {string} rawValue - Raw input value.
 * @param {object} options - Decimal length constraints.
 * @param {number} options.maxFractionDigits - Maximum digits allowed after the decimal point.
 * @param {number} [options.maxIntegerDigits] - Maximum digits allowed before the decimal point.
 * @param {number} [options.maxChars] - Maximum total character count when integer digits are derived from a total length cap.
 * @returns {string} Normalized decimal text.
 */
export function normalizeDecimalInput(
  rawValue: string,
  options: {
    maxFractionDigits: number;
    maxIntegerDigits?: number;
    maxChars?: number;
  },
): string {
  const { maxFractionDigits, maxIntegerDigits, maxChars } = options;
  const cleanedValue = rawValue.replace(/[^\d.]/g, "");
  const firstDecimalIndex = cleanedValue.indexOf(".");
  const normalizedValue =
    firstDecimalIndex === -1
      ? cleanedValue
      : `${cleanedValue.slice(0, firstDecimalIndex + 1)}${cleanedValue
          .slice(firstDecimalIndex + 1)
          .replace(/\./g, "")}`;

  if (normalizedValue === "") {
    return normalizedValue;
  }

  if (normalizedValue === ".") {
    return "0.";
  }

  const decimalIndex = normalizedValue.indexOf(".");

  if (decimalIndex === -1) {
    const normalizedIntegerDigits =
      normalizedValue === "" ? "0" : String(parseInt(normalizedValue, 10) || 0);

    if (maxIntegerDigits !== undefined) {
      return normalizedIntegerDigits.slice(0, maxIntegerDigits);
    }

    if (maxChars !== undefined) {
      return normalizedIntegerDigits.slice(0, maxChars);
    }

    return normalizedIntegerDigits;
  }

  const integerPart = normalizedValue.slice(0, decimalIndex);
  const integerDigits =
    integerPart === "" ? "0" : String(parseInt(integerPart, 10) || 0);
  const fractionDigits = normalizedValue
    .slice(decimalIndex + 1)
    .slice(0, maxFractionDigits);
  const allowedIntegerDigits =
    maxIntegerDigits ??
    (maxChars !== undefined
      ? Math.max(0, maxChars - 1 - fractionDigits.length)
      : undefined);

  if (allowedIntegerDigits === undefined) {
    return `${integerDigits}.${fractionDigits}`;
  }

  return `${integerDigits.slice(0, allowedIntegerDigits)}.${fractionDigits}`;
}

/**
 * Prevents input that would exceed specified limits
 * @param {InputKeyLimiterProps} props object
 * @returns {void} Does not return a value.
 */
export function InputKeyLimiter(props: InputKeyLimiterProps): void {
  const {
    event,
    allowNegative,
    maxChars,
    maxValue,
    allowDecimal = true,
    maxDecimalPlace,
  } = props;
  const { value } = event.currentTarget;
  const { start, end } = getNormalizedSelectionRange(event.currentTarget);
  const { key } = event;
  //this is for copy paste
  if (event.ctrlKey || event.metaKey) {
    return; // Allow Ctrl+V (Windows) or Cmd+V (Mac)
  }
  const preventKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];

  if (preventKeys.includes(key)) {
    // Allow essential control keys (navigation & editing)
    return;
  }

  // Prevent exceeding max character length for both text & number inputs
  if (maxChars && value.length >= maxChars) {
    event.preventDefault();

    return;
  }
  // Handle number inputs separately
  if (maxValue) {
    // Handle negative sign FIRST, before regex validation
    if (key === "-") {
      // Only allow negative if enabled and only at the beginning
      if (!allowNegative || start !== 0 || value.includes("-")) {
        event.preventDefault();
      }

      return;
    }

    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();

      return;
    }
    if (key === "ArrowUp" && +value === maxValue) {
      event.preventDefault();

      return;
    }

    if (key === "." && !allowDecimal) {
      event.preventDefault();

      return;
    }

    // Prevent multiple decimal points
    if (key === "." && value.includes(".") && allowDecimal) {
      event.preventDefault();

      return;
    }

    // Form new potential value by replacing selected text
    const beforeSelection = value.substring(0, start);
    const afterSelection = value.substring(end);
    const newValue = (beforeSelection + key + afterSelection).replace(
      /^0+/,
      "0",
    ); // Remove leading zeros to prevent issues

    // Check decimal places limit if maxDecimalPlace is defined
    if (!validateDecimalPlaces(newValue, maxDecimalPlace, allowDecimal)) {
      event.preventDefault();

      return;
    }

    // Ensure the number is within the limit
    if (!isNaN(Number(newValue)) && Math.abs(Number(newValue)) > maxValue) {
      event.preventDefault();
    }
  }
}

/**
 * Extracts validation error message from react-hook-form error object
 * Handles both direct message and nested root message structures
 * @param {unknown} error - The error object to check
 * @returns {string | undefined} The extracted error message or undefined
 */
export const getErrorMessage = (error: unknown): string | undefined => {
  if (!error) {
    return undefined;
  }

  const err = error as { message?: string; root?: { message?: string } };

  // Check for root message (often used for array validation errors)
  if (err.root?.message && typeof err.root.message === "string") {
    return err.root.message;
  }

  // Check for direct message (standard field validation errors)
  if (err.message && typeof err.message === "string") {
    return err.message;
  }

  return undefined;
};

/**
 *
 * @param {InputPasteLimiterProps} props object
 * @returns {void} Does not return a value.
 */
export function InputPasteLimiter(props: InputPasteLimiterProps): void {
  const {
    event,
    allowNegative,
    maxChars,
    maxValue,
    allowDecimal = true,
    maxDecimalPlace,
  } = props;

  const { value } = event.currentTarget;
  const { start, end } = getNormalizedSelectionRange(event.currentTarget);
  const clipboardData = event.clipboardData.getData("text");

  // If no clipboard data, allow the paste
  if (!clipboardData) {
    return;
  }

  // Calculate the new value after paste
  const beforeSelection = value.substring(0, start);
  const afterSelection = value.substring(end);
  const newValue = beforeSelection + clipboardData + afterSelection;

  // Check max character length
  if (maxChars && newValue.length > maxChars) {
    event.preventDefault();

    return;
  }

  // Handle number inputs separately
  if (maxValue) {
    // Check if pasted content contains only valid number characters
    const validNumberRegex = allowNegative ? /^-?[0-9.]*$/ : /^[0-9.]*$/;

    if (!validNumberRegex.test(clipboardData)) {
      event.preventDefault();

      return;
    }

    // Check decimal restrictions
    if (!allowDecimal && clipboardData.includes(".")) {
      event.preventDefault();

      return;
    }

    // Check for multiple decimal points in the final value
    if (allowDecimal && (newValue.match(/\./g) || []).length > 1) {
      event.preventDefault();

      return;
    }

    // Check negative sign restrictions
    if (clipboardData.includes("-")) {
      // Check if negative is allowed
      if (!allowNegative) {
        event.preventDefault();

        return;
      }

      // Check if negative sign is in correct position (beginning)
      const negativeIndex = clipboardData.indexOf("-");
      if (negativeIndex !== 0 || start !== 0) {
        event.preventDefault();

        return;
      }

      // Check if there's already a negative sign in the value
      if (value.includes("-")) {
        event.preventDefault();

        return;
      }

      // Check for multiple negative signs in pasted content
      if ((clipboardData.match(/-/g) || []).length > 1) {
        event.preventDefault();

        return;
      }
    }

    // Remove leading zeros for validation (but preserve single zero)
    const cleanedNewValue = newValue.replace(/^0+/, "") || "0";
    const finalValue = cleanedNewValue.startsWith(".")
      ? "0" + cleanedNewValue
      : cleanedNewValue;

    // Check decimal places limit if maxDecimalPlace is defined
    if (!validateDecimalPlaces(finalValue, maxDecimalPlace, allowDecimal)) {
      event.preventDefault();

      return;
    }

    // Check if the final number exceeds the maximum value
    if (!isNaN(Number(finalValue)) && Math.abs(Number(finalValue)) > maxValue) {
      event.preventDefault();

      return;
    }
  }
}

/**
 * @param {object} data Object to be converted to FormData
 * @param {string[]} imgKey Array of keys that are images
 * @param {object} dirtyFields Object of dirty fields to be sent. If you want to send all fields, pass an empty object
 * @returns {FormData} - The FormData object
 * @example
 * const data = {
 *  name: "John Doe",
 *  image: [File]
 * }
 *
 * const formData = parseToFormData(data, ["image"])
 */
export const parseToFormData = (
  data: {
    [key: string]: Any;
  },
  imgKey?: string[],
  // dirtyFields?: Record<string, boolean | Record<string, boolean>>,

  dirtyFields?: object,
): FormData => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (
      dirtyFields &&
      Object.keys(dirtyFields).length > 0 &&
      !dirtyFields[key as keyof typeof dirtyFields]
    ) {
      return;
    }

    if (imgKey?.includes(key)) {
      if (typeof value === "string") {
        if (value.startsWith("http")) {
          formData.append(key, value);

          return;
        }
      }
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        value === "null" ||
        (Array.isArray(value) && value.some((val) => val === "null"))
      ) {
        formData.append(key, "null");

        return;
      }
      if (Array.isArray(value) && !value?.length) {
        formData.append(key, "null");

        return;
      }
      if (Array.isArray(value) && value.length > 1) {
        for (let i = 0; i < value.length; i++) {
          formData.append(key, value[i]);
        }

        return;
      }
      if (value?.[0]) {
        formData.append(key, value?.[0]);

        return;
      } else if (value) {
        formData.append(key, value || null);

        return;
      }
    }

    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));

      return;
    } else if (value instanceof FileList) {
      for (let i = 0; i < value.length; i++) {
        formData.append(key, value[i]);
      }
    } else if (typeof value === "object" && value !== null) {
      if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else {
      // if (String(value) && typeof value !== "undefined") {

      formData.append(key, String(value));

      // }
    }
  });

  return formData;
};

export const imagesToUpdate = (
  images: (string | File)[] | undefined,
): {
  existingImages: (string | File)[];
  newImages: File[];
} => {
  let existingImages =
    Array.isArray(images) &&
    images?.filter((image) => typeof image === "string" && isHttps(image));

  let newImages = images?.filter((image) => typeof image !== "string");

  if (!existingImages) {
    existingImages = [];
  }
  if (!newImages) {
    newImages = [];
  }

  return { existingImages, newImages };
};

const isValidErrorData = (data: unknown): data is GeneralErrorResponse => {
  return GeneralErrorSchema.safeParse(data).success;
};

const getResponseMessage = (response: MutationResponse): string | undefined => {
  const responseData = response?.data;

  if (
    responseData &&
    typeof responseData === "object" &&
    "message" in responseData &&
    typeof responseData.message === "string"
  ) {
    return responseData.message;
  }

  return undefined;
};

const getErrorData = (response: MutationResponse): unknown => {
  const responseError = response?.error;

  if (
    responseError &&
    typeof responseError === "object" &&
    "data" in responseError
  ) {
    return responseError.data;
  }

  return undefined;
};

const isSuccessfulMutationResponse = (response: MutationResponse): boolean => {
  return (
    response !== undefined &&
    response !== null &&
    typeof response === "object" &&
    "data" in response &&
    response.data !== undefined &&
    !("error" in response)
  );
};

export const validateResponse = ({
  response,
  onSuccess,
  onError,
}: ValidateResponseProps): boolean => {
  if (isSuccessfulMutationResponse(response)) {
    const responseMessage = getResponseMessage(response);

    onSuccess(responseMessage);

    return true;
  }

  const errorData = getErrorData(response);

  if (onError) {
    if (isValidErrorData(errorData)) {
      onError(errorData);
    } else {
      onError({
        message: "Something went Wrong",
      });
    }
  }

  return false;
};

/**
 * @description It filters out empty values from an object or array while retaining valid falsy values like `0` and `false`.
 * @param {T} input - Input object or array
 * @param {FilterEmptyValuesOptions<T>} options - Filtering options
 * @returns {FilterEmpty<T>} - Filtered object or array
 */
export const filterEmptyValues = <T>(
  input: T,
  options: FilterEmptyValuesOptions<T> = {},
): FilterEmpty<T> => {
  if (Array.isArray(input)) {
    return input
      .map((item) => {
        // Recursively filter empty values
        if (Array.isArray(item)) {
          return filterEmptyValues(
            item,
            options as FilterEmptyValuesOptions<typeof item>,
          );
        }
        if (
          item instanceof File ||
          (typeof item === "string" && item?.length > 0) ||
          item instanceof Date ||
          item instanceof Blob
        ) {
          return item; // Keep Files , strings and Dates
        }
        if (typeof item === "object" && item !== null) {
          return filterEmptyValues(
            item,
            options as FilterEmptyValuesOptions<typeof item>,
          ); // Recurse into objects
        }

        return item !== null && item !== undefined && item !== ""
          ? item
          : undefined; // Keep falsy but valid values like 0, false, etc.
      })
      .filter((item) => item !== undefined) as FilterEmpty<T>; // Filter out undefined
  }

  if (typeof input === "object" && input !== null) {
    return Object.entries(input)
      .map(([key, value]) => {
        if (
          options.avoidKeys?.[
            key as keyof NonNullable<typeof options.avoidKeys>
          ]
        ) {
          return [key, value];
        }

        // Recursively filter empty values
        if (
          value instanceof File ||
          (typeof value === "string" && value?.length > 0) ||
          value instanceof Date ||
          value instanceof Blob
        ) {
          return [key, value]; // Keep Files , strings and Dates
        }
        if (Array.isArray(value)) {
          const filteredArray = filterEmptyValues(
            value,
            options as FilterEmptyValuesOptions<typeof value>,
          );

          return filteredArray.length > 0 ? [key, filteredArray] : undefined; // Include non-empty arrays
        }
        if (typeof value === "object" && value !== null) {
          const filteredObject = filterEmptyValues(
            value,
            options as FilterEmptyValuesOptions<typeof value>,
          );

          return Object.keys(filteredObject).length > 0
            ? [key, filteredObject]
            : undefined; // Include non-empty objects
        }

        return value !== null && value !== undefined && value !== ""
          ? [key, value]
          : undefined; // Keep falsy but valid values
      })
      .filter((item) => item !== undefined) // Filter out undefined
      .reduce((acc, [key, value]) => {
        // @ts-expect-error - We know that key is a string
        acc[key] = value; // Construct the filtered object

        return acc;
      }, {} as FilterEmpty<T>);
  }

  return input as FilterEmpty<T>; // Return the input if it's not an object or array
};

export type DirtyFields<T> = Partial<{
  [K in keyof T]: T[K] extends Record<string, unknown>
    ? DirtyFields<T[K]> | boolean
    : boolean;
}>;

/**
 * Returns whether a react-hook-form dirty marker contains any changed leaf field.
 *
 * @param {unknown} dirtyField - Dirty marker from react-hook-form, possibly nested.
 * @returns {boolean} True when at least one nested field is marked dirty.
 */
function hasDirtyFieldMarker(dirtyField: unknown): boolean {
  if (dirtyField === true) {
    return true;
  }

  if (Array.isArray(dirtyField)) {
    return dirtyField.some(hasDirtyFieldMarker);
  }

  if (dirtyField && typeof dirtyField === "object") {
    return Object.values(dirtyField).some(hasDirtyFieldMarker);
  }

  return false;
}

/**
 * Recursively extracts the values of fields marked as "dirty" from the provided `values` object.
 * @template DirtyFields - An object indicating which fields are dirty.
 * @template Values - An object containing the corresponding values.
 * @param {DirtyFields} dirtyFields - Object marking dirty fields, possibly nested.
 * @param {Values} values - Object containing actual values corresponding to the dirty fields.
 * @returns {Partial<Values>} An object containing only the values of dirty fields.
 */
export function getDirtyValues<T extends Record<string, unknown>>(
  dirtyFields: DirtyFields<T>,
  values: T,
): Partial<T> {
  // Guard against undefined or null values
  if (!values || !dirtyFields) {
    return {} as Partial<T>;
  }

  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    const typedKey = key as keyof T;
    const dirtyField = dirtyFields[typedKey];

    if (!hasDirtyFieldMarker(dirtyField)) {
      return prev;
    }

    // Check if the value exists before processing
    const value = values[typedKey];

    if (typeof dirtyField === "object" && dirtyField !== null) {
      // If the value is undefined/null, skip or return the dirty field indication
      if (value === undefined || value === null) {
        return {
          ...prev,
          [key]: value,
        };
      }

      if (Array.isArray(value)) {
        return {
          ...prev,
          [key]: value,
        };
      }

      const nestedDirtyValues = getDirtyValues(
        //@ts-expect-error - TS cannot infer that `values[typedKey]` is a Record when `dirtyField` is an object, which is required for the recursive call.
        dirtyField as DirtyFields<T[typeof typedKey]>,
        value as T[typeof typedKey],
      );

      if (Object.keys(nestedDirtyValues).length === 0) {
        return prev;
      }

      return {
        ...prev,
        [key]: nestedDirtyValues,
      };
    }

    return {
      ...prev,
      [key]: value,
    };
  }, {} as Partial<T>);

  return dirtyValues;
}

/**
 * Checks if the node is a field error
 * @param {unknown} node - The node to check
 * @returns {boolean} - True if the node is a field error, false otherwise
 */
function isFieldError(node: unknown): node is { message: string } {
  return (
    typeof node === "object" &&
    node !== null &&
    "message" in node &&
    typeof node.message === "string"
  );
}

/**
 * Recursively extracts field names from form errors
 * @param {Record<string, unknown>} errors - Form errors object
 * @param {string} [prefix] - Prefix for nested field names
 * @returns {string[]} Array of formatted field names
 */
export function extractFieldNames(
  errors: Record<string, unknown>,
  prefix = "",
): string[] {
  const results: string[] = [];

  for (const key of Object.keys(errors)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const node = errors[key];

    if (!node) {
      continue;
    }

    if (isFieldError(node)) {
      results.push(path);
      continue;
    }

    if (typeof node === "object") {
      results.push(...extractFieldNames(node as Record<string, unknown>, path));
    } else {
      results.push(path);
    }
  }

  return results;
}

/**
 * Formats an array of field names into a readable comma-separated string
 * Extracts the last segment from dot-notation field paths and capitalizes them
 * @param {string[]} fieldNames - Array of field names (may include dot-notation paths)
 * @returns {string} - Formatted comma-separated string of field names
 * @example
 * formatFieldList(["user.name", "user.email", "age"])
 * // Returns: "Name, Email, Age"
 */
export function formatFieldList(fieldNames: string[]): string {
  return fieldNames
    .map((f) => formatToCapitalizedWords(f.split(".").pop()!))
    .join(", ");
}

/**
 * Checks for form validation errors and displays a toast error message
 * Lists specific fields that have errors
 * @param {HandleFormValidationErrorsProps} props - Object containing errors and optional custom message
 * @returns {boolean} - Returns true if there are errors, false otherwise
 */
export function handleFormValidationErrors({
  errors,
  customMessage,
}: HandleFormValidationErrorsProps): boolean {
  if (!errors || Object.keys(errors).length === ZERO) {
    return false;
  }

  const fieldNames = extractFieldNames(errors).filter(Boolean);

  const buildDefaultMessage = (list: string[]): string => {
    const isSingle = list.length === ONE;
    const term = isSingle ? "field" : "fields";

    return `Please fill in the required ${term}: ${formatFieldList(list)}`;
  };

  const getFinalMessage = (): string => {
    if (typeof customMessage === "string" && customMessage) {
      return customMessage;
    }

    if (
      fieldNames.length > ZERO &&
      typeof customMessage === "object" &&
      customMessage !== null
    ) {
      const standardFieldLabels: string[] = [];
      const specificMessages: string[] = [];

      fieldNames.forEach((field) => {
        const rootKey = field.split(".")[ZERO];
        const mapping = customMessage[field] || customMessage[rootKey];

        if (mapping) {
          if (mapping.includes(" ")) {
            specificMessages.push(mapping);
          } else {
            standardFieldLabels.push(mapping);
          }
        } else {
          standardFieldLabels.push(field);
        }
      });

      const uniqueLabels = Array.from(new Set(standardFieldLabels));
      const uniqueSpecificMessages = Array.from(new Set(specificMessages));

      if (uniqueLabels.length > ZERO) {
        return buildDefaultMessage(uniqueLabels);
      }

      if (uniqueSpecificMessages.length > ZERO) {
        return uniqueSpecificMessages[ZERO];
      }
    }

    if (fieldNames.length > ZERO) {
      return buildDefaultMessage(fieldNames);
    }

    return "Please fill in all required fields.";
  };

  const errorMessage = getFinalMessage();
  customToast.error(errorMessage);

  return true;
}

/**
 * Creates a generic error handler for react-hook-form submit errors
 * This can be used as the second parameter in handleSubmit(onSubmit, onError)
 * @param {string | Record<string, string>} customMessage - Optional custom error message for all fields, or a record of field-specific messages.
 * @returns {SubmitErrorHandler<TFieldValues>} - Error handler function
 * @example
 * const onError = createFormErrorHandler("Please complete all required fields");
 * // or
 * const onError = createFormErrorHandler({
 *   address: "Please select a valid address from the dropdown list.",
 * });
 * form.handleSubmit(onSubmit, onError);
 */
export function createFormErrorHandler<
  TFieldValues extends Record<string, unknown>,
>(
  customMessage?: string | Record<string, string>,
): SubmitErrorHandler<TFieldValues> {
  return (errors) => {
    handleFormValidationErrors({
      errors: errors as Record<string, unknown>,
      customMessage,
    });
  };
}

/**
 *
 * @param {number} value - The value to format
 * @param {number} decimalPlaces - The number of decimal places to format to
 * @returns {string} - The formatted value
 */
export function formatDecimal(
  value: number | undefined | null,
  decimalPlaces: number = PERCENT_MAX_DECIMAL_PLACES,
): string {
  const num = Number.isFinite(value) ? (value as number) : 0;

  return new Decimal(new Decimal(num).toFixed(decimalPlaces)).toString();
}

/**
 * Maps `""` and `null` to `undefined` so Zod `required_error` applies instead of
 * `Number(null)` / `Number("")` becoming `0`.
 * @param {unknown} val - Raw field value from the form or API.
 * @returns {unknown} `undefined` when empty string or null; otherwise `val`.
 */
export function preprocessEmptyOrNullToUndefined(val: unknown): unknown {
  if (val === "" || val === null) {
    return undefined;
  }

  return val;
}
